import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { upsertSiteConfig } from '@/services/readdyBackend';

export interface SiteTexts {
  // Navbar
  navHome: string;
  navImoveis: string;
  navLancamento: string;
  navSobreNos: string;
  navContato: string;
  navBlog: string;
  navFaq: string;
  // Hero
  heroTitle: string;
  heroSubtitle: string;
  heroBtnComprar: string;
  heroBtnAlugar: string;
  heroBtnLancamentos: string;
  // About Preview
  aboutTitle: string;
  aboutText1: string;
  aboutText2: string;
  aboutStat1Value: string;
  aboutStat1Label: string;
  aboutStat2Value: string;
  aboutStat2Label: string;
  aboutStat3Value: string;
  aboutStat3Label: string;
  aboutBtn: string;
  // Services
  servicesTitle: string;
  servicesSubtitle: string;
  // CTA
  ctaTitle: string;
  ctaSubtitle: string;
  ctaBtnContato: string;
  ctaBtnWhatsapp: string;
  ctaPhone: string;
  ctaInstagram: string;
  // Footer
  footerDescription: string;
  footerLinksTitle: string;
  footerContactTitle: string;
  footerHoursTitle: string;
  footerAddress: string;
  footerPhone: string;
  footerInstagram: string;
  footerHours1: string;
  footerHours2: string;
  footerHours3: string;
  footerCnpj: string;
  footerCopyright: string;
  // Sobre Nós
  sobreHeroTitle: string;
  sobreHeroSubtitle: string;
  sobreHistoriaTitle: string;
  sobreHistoriaText1: string;
  sobreHistoriaText2: string;
  sobreHistoriaText3: string;
  sobreHistoriaText4: string;
  sobreStat1Value: string;
  sobreStat1Label: string;
  sobreStat2Value: string;
  sobreStat2Label: string;
  sobreStat3Value: string;
  sobreStat3Label: string;
  // Contato
  contatoTitle: string;
  contatoSubtitle: string;
  contatoPhone: string;
  contatoEmail: string;
  contatoAddress: string;
  contatoHours: string;
  // Imoveis page
  imoveisHeroTitle: string;
  imoveisHeroSubtitle: string;
  // WhatsApp
  whatsappNumber: string;
}

export interface SiteColors {
  primary: string;
  primaryHover: string;
  dark: string;
}

export interface SiteConfig {
  logoUrl: string;
  logoUrlDark: string;
  colors: SiteColors;
  texts: SiteTexts;
}

const DEFAULT_CONFIG: SiteConfig = {
  logoUrl: 'https://static.readdy.ai/image/901e621bd1cb38df7a91d2b658ce7f94/663e15e7378507e10e2e15b23c0835fe.png',
  logoUrlDark: 'https://static.readdy.ai/image/901e621bd1cb38df7a91d2b658ce7f94/8a4b3b07880c96c4ff8adbf62a7a756d.jpeg',
  colors: {
    primary: '#df862c',
    primaryHover: '#c97520',
    dark: '#1a2332',
  },
  texts: {
    navHome: 'Home',
    navImoveis: 'Imóveis',
    navLancamento: 'Lançamento',
    navSobreNos: 'Sobre Nós',
    navContato: 'Contato',
    navBlog: 'Blog',
    navFaq: 'FAQ',
    heroTitle: 'Encontre o Imóvel dos Seus Sonhos',
    heroSubtitle: 'Compra, venda e locação de imóveis com qualidade e confiança',
    heroBtnComprar: 'Comprar',
    heroBtnAlugar: 'Alugar',
    heroBtnLancamentos: 'Lançamentos',
    aboutTitle: 'Mais de 10 Anos de Experiência',
    aboutText1: 'Somos uma imobiliária consolidada no mercado, oferecendo serviços completos de compra, venda e locação de imóveis. Nossa missão é conectar pessoas aos seus lares ideais com transparência, profissionalismo e dedicação.',
    aboutText2: 'Com uma equipe altamente qualificada e comprometida, trabalhamos para superar as expectativas de nossos clientes, oferecendo assessoria completa em todas as etapas do processo imobiliário.',
    aboutStat1Value: '5000+',
    aboutStat1Label: 'Imóveis Vendidos',
    aboutStat2Value: '98%',
    aboutStat2Label: 'Clientes Satisfeitos',
    aboutStat3Value: '10+',
    aboutStat3Label: 'Anos de Mercado',
    aboutBtn: 'Conheça Nossa História',
    servicesTitle: 'Nossos Serviços',
    servicesSubtitle: 'Oferecemos soluções completas para todas as suas necessidades imobiliárias',
    ctaTitle: 'Pronto para Encontrar Seu Próximo Imóvel?',
    ctaSubtitle: 'Entre em contato com a Ação Negócios Imobiliários e realize o seu sonho em Luís Eduardo Magalhães - BA',
    ctaBtnContato: 'Fale Conosco',
    ctaBtnWhatsapp: 'Chamar no WhatsApp',
    ctaPhone: '(77) 99963-3110',
    ctaInstagram: '@acaonegociosimobiliarios',
    footerDescription: 'Compra, Venda, Locação e Administração de imóveis em Luís Eduardo Magalhães - BA.',
    footerLinksTitle: 'Links Rápidos',
    footerContactTitle: 'Contato',
    footerHoursTitle: 'Horário de Atendimento',
    footerAddress: 'Rua Paraíba, n° 890, Centro\nLuís Eduardo Magalhães - BA',
    footerPhone: '(77) 99963-3110',
    footerInstagram: '@acaonegociosimobiliarios',
    footerHours1: 'Segunda a Sexta: 8h às 18h',
    footerHours2: 'Sábado: 8h às 12h',
    footerHours3: 'Domingo: Fechado',
    footerCnpj: '23.571.591/0001-52',
    footerCopyright: '© 2026 Ação Negócios Imobiliários. Todos os direitos reservados.',
    sobreHeroTitle: 'Sobre Nós',
    sobreHeroSubtitle: 'Atuando com excelência no mercado imobiliário de Luís Eduardo Magalhães desde 2015',
    sobreHistoriaTitle: 'Nossa História',
    sobreHistoriaText1: 'A Ação Negócios Imobiliários é uma empresa consolidada no mercado imobiliário de Luís Eduardo Magalhães – BA, atuando com excelência desde 2015. Fundada por Elton Juliani, corretor devidamente registrado no CRECI/BA sob o nº 022.620, a empresa se destaca pela seriedade, transparência e compromisso com seus clientes.',
    sobreHistoriaText2: 'Desde a sua fundação, trabalhamos com administração de imóveis comerciais e residenciais, vendas de imóveis urbanos e rurais, além de locações diversas, sempre buscando oferecer soluções completas e seguras no mercado imobiliário.',
    sobreHistoriaText3: 'Estamos localizados na Rua Paraíba, nº 890, Centro, sobreloja sala 02 – CEP: 47.850-047, em um espaço preparado para melhor atendê-lo.',
    sobreHistoriaText4: 'Venha nos fazer uma visita! Contamos com uma equipe de profissionais qualificados, prontos para oferecer um atendimento personalizado e eficiente.',
    sobreStat1Value: '2015',
    sobreStat1Label: 'Fundação',
    sobreStat2Value: '10+',
    sobreStat2Label: 'Anos de Mercado',
    sobreStat3Value: 'CRECI',
    sobreStat3Label: '1857 PJ',
    contatoTitle: 'Entre em Contato',
    contatoSubtitle: 'Estamos prontos para ajudar você a encontrar o imóvel ideal',
    contatoPhone: '(77) 99963-3110',
    contatoEmail: 'imobiliariacaoba@hotmail.com',
    contatoAddress: 'Rua Paraíba, n° 890, Centro, Luís Eduardo Magalhães - BA',
    contatoHours: 'Seg–Sex: 8h às 18h | Sáb: 8h às 12h',
    imoveisHeroTitle: 'Encontre Seu Imóvel Ideal',
    imoveisHeroSubtitle: 'Explore nossa carteira completa de imóveis em Luís Eduardo Magalhães',
    whatsappNumber: '5577999633110',
  },
};

interface SiteConfigContextType {
  config: SiteConfig;
  loading: boolean;
  updateConfig: (newConfig: Partial<SiteConfig>) => Promise<void>;
  updateTexts: (texts: Partial<SiteTexts>) => void;
  updateColors: (colors: Partial<SiteColors>) => void;
  updateLogo: (logoUrl: string, logoUrlDark: string) => void;
  resetConfig: () => Promise<void>;
}

const SiteConfigContext = createContext<SiteConfigContextType | null>(null);

async function saveConfig(config: SiteConfig) {
  await Promise.all([
    upsertSiteConfig('logo_url', config.logoUrl),
    upsertSiteConfig('logo_url_dark', config.logoUrlDark),
    upsertSiteConfig('colors_json', JSON.stringify(config.colors)),
    upsertSiteConfig('texts_json', JSON.stringify(config.texts)),
  ]);
}

export function SiteConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<SiteConfig>(() => {
    try {
      const saved = localStorage.getItem('site_config');
      if (saved) {
        const parsed = JSON.parse(saved);
        const mergedTexts = { ...DEFAULT_CONFIG.texts, ...parsed.texts };
        return { ...DEFAULT_CONFIG, ...parsed, texts: mergedTexts, colors: { ...DEFAULT_CONFIG.colors, ...parsed.colors } };
      }
    } catch {
      // ignore
    }
    return DEFAULT_CONFIG;
  });
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Load from Supabase on mount (SELECT is public, works fine)
  useEffect(() => {
    async function loadConfig() {
      try {
        const { data, error } = await supabase
          .from('site_config')
          .select('key, value')
          .in('key', ['logo_url', 'logo_url_dark', 'colors_json', 'texts_json']);

        if (error) {
          console.warn('SiteConfig: Supabase read error:', error);
        } else if (data && data.length > 0) {
          const map: Record<string, string> = {};
          data.forEach((row) => { map[row.key] = row.value; });

          const loaded: Partial<SiteConfig> = {};
          if (map.logo_url) loaded.logoUrl = map.logo_url;
          if (map.logo_url_dark) loaded.logoUrlDark = map.logo_url_dark;
          if (map.colors_json) {
            try { loaded.colors = JSON.parse(map.colors_json); } catch { /* ignore */ }
          }
          if (map.texts_json) {
            try { loaded.texts = JSON.parse(map.texts_json); } catch { /* ignore */ }
          }

          const mergedTexts = { ...DEFAULT_CONFIG.texts, ...(loaded.texts || {}) };
          const mergedColors = { ...DEFAULT_CONFIG.colors, ...(loaded.colors || {}) };
          setConfig({
            ...DEFAULT_CONFIG,
            ...loaded,
            texts: mergedTexts,
            colors: mergedColors,
          });
        }
        // No need to create defaults on first load — admin will save via edge function
      } catch (err) {
        console.warn('SiteConfig: Network error, using defaults:', err);
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    }
    loadConfig();
  }, []);

  // Apply CSS variables and save to localStorage whenever config changes
  useEffect(() => {
    localStorage.setItem('site_config', JSON.stringify(config));
    document.documentElement.style.setProperty('--color-primary', config.colors.primary);
    document.documentElement.style.setProperty('--color-primary-hover', config.colors.primaryHover);
    document.documentElement.style.setProperty('--color-dark', config.colors.dark);
  }, [config]);

  // Save only explicit administrator changes. Reading the public site must never write.
  const updateConfig = async (newConfig: Partial<SiteConfig>) => {
    const next = { ...config, ...newConfig };
    await saveConfig(next);
    setConfig(next);
  };

  const updateTexts = (texts: Partial<SiteTexts>) => {
    setConfig((prev) => ({ ...prev, texts: { ...prev.texts, ...texts } }));
  };

  const updateColors = (colors: Partial<SiteColors>) => {
    setConfig((prev) => ({ ...prev, colors: { ...prev.colors, ...colors } }));
  };

  const updateLogo = (logoUrl: string, logoUrlDark: string) => {
    setConfig((prev) => ({ ...prev, logoUrl, logoUrlDark }));
  };

  const resetConfig = async () => {
    await saveConfig(DEFAULT_CONFIG);
    setConfig(DEFAULT_CONFIG);
  };

  return (
    <SiteConfigContext.Provider value={{ config, loading, updateConfig, updateTexts, updateColors, updateLogo, resetConfig }}>
      {children}
    </SiteConfigContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSiteConfig() {
  const ctx = useContext(SiteConfigContext);
  if (!ctx) throw new Error('useSiteConfig must be used within SiteConfigProvider');
  return ctx;
}

export { DEFAULT_CONFIG };